// src/Components/Drawer/InfoTab/InfoTab.js

import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  Stack,
  Backdrop,
  CircularProgress,
  IconButton,
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { collection, query, orderBy, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { db } from '../../../Firebase/firebase';
import ConfirmDialog from '../../ConfirmDialog/ConfirmDialog';

import { RenderSelect } from './RenderSelect';

const DARK_BLUE = '#00008B';
const ORANGE_COLOR = '#FF9900';

const styles = {
  fieldContainer: { display: 'flex', flexDirection: 'column', width: '100%' },
  label: {
    color: DARK_BLUE,
    fontWeight: 'bold',
    fontSize: '0.65rem',
    textTransform: 'uppercase',
    marginBottom: '0px',
    marginLeft: '2px',
    lineHeight: 1.2,
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  valueBox: {
    borderRadius: '4px',
    minHeight: '24px',
    display: 'flex',
    alignItems: 'center',
    padding: '0 4px',
    overflow: 'hidden',
    transition: 'background-color 0.3s ease',
    border: `1px solid ${ORANGE_COLOR}`,
  },
  input: {
    border: 'none',
    outline: 'none',
    width: '100%',
    backgroundColor: 'transparent',
    fontSize: '0.8rem',
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    color: DARK_BLUE,
    padding: 0,
    margin: 0,
  },
  activeInput: {
    cursor: 'text',
  },
};

// AMENDED: Upgraded to a functional component with refs to manage cursor snapping
const RenderField = ({
  label,
  value,
  fieldKey,
  isEditing,
  onChange,
  type = 'text',
  multiline = false,
  endNode = null,
}) => {
  const inputRef = useRef(null);
  const cursorPositionRef = useRef(null);

  // Restore cursor position after a state-driven re-render
  useEffect(() => {
    if (inputRef.current && cursorPositionRef.current !== null) {
      try {
        inputRef.current.setSelectionRange(
          cursorPositionRef.current,
          cursorPositionRef.current,
        );
      } catch (e) {
        // Safe catch for input types (like 'date') that do not support selection ranges
      }
      cursorPositionRef.current = null;
    }
  }, [value]);

  const handleChange = (e) => {
    // Capture the cursor position precisely when the user types
    cursorPositionRef.current = e.target.selectionStart;
    onChange(fieldKey, e.target.value, e.nativeEvent, e.target.selectionStart);
  };

  return (
    <Box sx={styles.fieldContainer}>
      <Typography sx={styles.label}>{label}</Typography>
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          gap: endNode ? 1 : 0,
          alignItems: multiline ? 'flex-start' : 'center',
        }}>
        <Box
          sx={{
            ...styles.valueBox,
            flexGrow: 1,
            backgroundColor: isEditing ? '#E3F2FD' : '#fff',
            alignItems: multiline ? 'flex-start' : 'center',
            padding: multiline ? '4px' : '0 4px',
            height: isEditing ? 'auto' : 'auto',
          }}>
          {multiline ? (
            isEditing ? (
              <textarea
                ref={inputRef}
                value={value || ''}
                onChange={handleChange}
                rows={3}
                style={{
                  ...styles.input,
                  ...styles.activeInput,
                  resize: 'vertical',
                }}
              />
            ) : (
              <Typography
                sx={{
                  ...styles.input,
                  whiteSpace: 'pre-wrap', // Preserves intentional line breaks
                  wordWrap: 'break-word',
                  minHeight: '44px', // Matches roughly 3 rows
                  py: '2px',
                }}>
                {value || ''}
              </Typography>
            )
          ) : isEditing ? (
            <input
              ref={inputRef}
              type={type}
              value={value || ''}
              onChange={handleChange}
              style={{
                ...styles.input,
                ...styles.activeInput,
              }}
            />
          ) : (
            <Typography
              sx={{
                ...styles.input,
                whiteSpace: 'normal',
                wordWrap: 'break-word',
                py: '2px',
              }}>
              {value || ''}
            </Typography>
          )}
        </Box>
        {endNode && <Box sx={{ flexShrink: 0 }}>{endNode}</Box>}
      </Box>
    </Box>
  );
};

export default function InfoTab({
  data = {},
  isEditing,
  onDataChange,
  isSaving,
  activeMenu,
}) {
  const [categories, setCategories] = useState([]);
  const [regions, setRegions] = useState([]);
  const [makeUppercase, setMakeUppercase] = useState(true);

  // State for URL Validation Trap
  const [showUrlError, setShowUrlError] = useState(false);

  // State for Batch Filename
  const [batchFilename, setBatchFilename] = useState('');
  const [batchSource, setBatchSource] = useState('');

  useEffect(() => {
    const unsubCat = onSnapshot(
      query(collection(db, 'categories'), orderBy('name')),
      (snap) => setCategories(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    );
    const unsubReg = onSnapshot(
      query(collection(db, 'region'), orderBy('name')),
      (snap) => setRegions(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    );

    // Fetch global formatting rules
    const unsubFmt = onSnapshot(doc(db, 'settings', 'TextFormatting'), (snap) => {
      if (snap.exists()) {
        const fmtData = snap.data();
        setMakeUppercase(
          fmtData.makeUppercase !== undefined ? !!fmtData.makeUppercase : true,
        );
      } else {
        setMakeUppercase(true);
      }
    });

    return () => {
      unsubCat();
      unsubReg();
      unsubFmt();
    };
  }, []);

  // Fetch Batch Filename from Imports or Archives if marker is attached to a batch
  useEffect(() => {
    const fetchBatchInfo = async () => {
      if (data.importBatchId) {
        try {
          let docRef = doc(db, 'excelImports', data.importBatchId);
          let docSnap = await getDoc(docRef);

          if (docSnap.exists()) {
            setBatchFilename(docSnap.data().filename || '');
            setBatchSource('imports');
            return;
          }

          docRef = doc(db, 'excelArchives', data.importBatchId);
          docSnap = await getDoc(docRef);

          if (docSnap.exists()) {
            setBatchFilename(docSnap.data().filename || '');
            setBatchSource('archives');
            return;
          }

          setBatchFilename('');
          setBatchSource('');
        } catch (e) {
          console.error('Error fetching batch filename:', e);
          setBatchFilename('');
          setBatchSource('');
        }
      } else {
        setBatchFilename('');
        setBatchSource('');
      }
    };

    fetchBatchInfo();
  }, [data.importBatchId]);

  // Set default date to today's date if it is empty upon opening
  useEffect(() => {
    if (data && (data.id || data.isNew) && !data.date && onDataChange) {
      const today = new Date().toISOString().substring(0, 10);
      onDataChange({ ...data, date: today });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.id, data?.isNew, data?.date]);

  const currentCategoryName = useMemo(
    () => categories.find((c) => c.id === data.categoryId)?.name || '',
    [data.categoryId, categories],
  );
  const currentRegionName = useMemo(
    () => regions.find((r) => r.id === data.regionId)?.name || '',
    [data.regionId, regions],
  );

  // AMENDED: Accept nativeEvent and selectionStart to apply advanced formatting rules
  const handleFieldChange = (key, value, nativeEvent, selectionStart) => {
    let finalValue = value;

    // Do not apply text formatting to these specific keys
    const excludedKeys = [
      'website',
      'categoryId',
      'regionId',
      'date',
      'showcase',
      'hilight',
      'lat',
      'lng',
    ];

    if (makeUppercase && !excludedKeys.includes(key) && typeof value === 'string') {
      // AMENDED: Handle Paste explicitly to prevent scrambling
      if (nativeEvent && nativeEvent.inputType === 'insertFromPaste') {
        if (nativeEvent.data && typeof selectionStart === 'number') {
          const dataLen = nativeEvent.data.length;
          const start = selectionStart - dataLen;
          if (start >= 0 && value.substring(start, selectionStart) === nativeEvent.data) {
            finalValue =
              value.slice(0, start) +
              nativeEvent.data.toUpperCase() +
              value.slice(selectionStart);
          } else {
            finalValue = value.toUpperCase();
          }
        } else {
          finalValue = value.toUpperCase();
        }
      }
      // AMENDED: Handle normal typing with "caps lock in reverse"
      else if (
        nativeEvent &&
        nativeEvent.data &&
        typeof selectionStart === 'number' &&
        nativeEvent.inputType === 'insertText'
      ) {
        const dataLen = nativeEvent.data.length;
        const start = selectionStart - dataLen;

        if (start >= 0 && value.substring(start, selectionStart) === nativeEvent.data) {
          const inverted = nativeEvent.data
            .split('')
            .map((c) => (c === c.toUpperCase() ? c.toLowerCase() : c.toUpperCase()))
            .join('');

          finalValue = value.slice(0, start) + inverted + value.slice(selectionStart);
        } else {
          // AMENDED: Safe fallback for mistimed insertText events
          finalValue = value.toUpperCase();
        }
      }
      // AMENDED: Handle backspace/deletions naturally without re-formatting
      else if (
        nativeEvent &&
        nativeEvent.inputType &&
        nativeEvent.inputType.startsWith('delete')
      ) {
        // Let deletion happen naturally
      } else {
        // AMENDED: Fallback for autofill, autocomplete, or other uncontrolled events
        // Replaced getReversedCaseString with standard toUpperCase to fix the autofill flip bug.
        finalValue = value.toUpperCase();
      }
    }

    let updates = { ...data, [key]: finalValue };
    if (key === 'categoryId') {
      const selectedCat = categories.find((cat) => cat.id === finalValue);
      if (selectedCat?.icon) {
        updates.icon = selectedCat.icon;
        updates.iconID = selectedCat.iconID || null;
      }
    }
    if (onDataChange) onDataChange(updates);
  };

  const handleOpenWeb = (url) => {
    if (!url || url.trim() === '') return;
    let finalUrl = url.trim();

    // Auto-prepend protocol if missing
    if (!/^https?:\/\//i.test(finalUrl)) {
      finalUrl = 'http://' + finalUrl;
    }

    try {
      const parsedUrl = new URL(finalUrl);

      // Strict check: Hostname must contain at least one dot (e.g., "google.com")
      // This prevents "http://abc" from passing validation.
      if (!parsedUrl.hostname.includes('.') && parsedUrl.hostname !== 'localhost') {
        throw new Error('Invalid Domain Structure');
      }

      window.open(finalUrl, '_blank', 'noopener,noreferrer');
    } catch (e) {
      console.warn('Invalid URL Format caught:', finalUrl);
      setShowUrlError(true);
    }
  };

  // Extract Lat / Lng exclusively for single-point markers
  let displayLat = '';
  let displayLng = '';
  if ((data.type || '').toLowerCase() === 'marker') {
    if (data.position && data.position.lat !== undefined) {
      displayLat =
        typeof data.position.lat === 'function' ? data.position.lat() : data.position.lat;
      displayLng =
        typeof data.position.lng === 'function' ? data.position.lng() : data.position.lng;
    } else if (data.lat !== undefined) {
      displayLat = data.lat;
      displayLng = data.lng;
    }
  }

  const handleCopyLatLng = () => {
    if (displayLat !== '' && displayLng !== '') {
      navigator.clipboard.writeText(`lat: ${displayLat}  lng: ${displayLng}`);
    }
  };

  if (!data || Object.keys(data).length === 0 || (!data.id && !data.isNew)) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography
          sx={{
            fontStyle: 'italic',
            fontSize: '0.85rem',
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
          }}>
          Click on a marker to add information, photos and documents.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <Backdrop
        sx={{ color: '#fff', zIndex: 100002, backgroundColor: 'transparent' }} // AMENDED: Elevated to global processing layer
        open={!!isSaving}>
        <CircularProgress sx={{ color: '#00B0FF' }} />
      </Backdrop>

      <Stack
        key={data.id}
        spacing={0.2}
        sx={{ p: 1, width: '100%', boxSizing: 'border-box' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Box sx={{ flex: 1 }}>
            <RenderField
              label='Date'
              value={
                typeof data.date === 'string' ? data.date.substring(0, 10) : data.date
              }
              fieldKey='date'
              isEditing={isEditing}
              onChange={handleFieldChange}
              type='date'
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography sx={styles.label}>Showcase</Typography>
            <Box
              sx={{
                ...styles.valueBox,
                width: '30px',
                padding: 0,
                justifyContent: 'center',
                backgroundColor: isEditing ? '#E3F2FD' : '#fff',
                cursor: isEditing ? 'pointer' : 'default',
              }}
              onClick={() => isEditing && handleFieldChange('showcase', !data.showcase)}>
              <Checkbox
                checked={!!data.showcase}
                size='small'
                sx={{
                  padding: 0,
                  '&.Mui-checked': { color: DARK_BLUE },
                  pointerEvents: 'none',
                }}
              />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography sx={styles.label}>Hilight</Typography>
            <Box
              sx={{
                ...styles.valueBox,
                width: '30px',
                padding: 0,
                justifyContent: 'center',
                backgroundColor: isEditing ? '#E3F2FD' : '#fff',
                cursor: isEditing ? 'pointer' : 'default',
              }}
              onClick={() => isEditing && handleFieldChange('hilight', !data.hilight)}>
              <Checkbox
                checked={!!data.hilight}
                size='small'
                sx={{
                  padding: 0,
                  '&.Mui-checked': { color: DARK_BLUE },
                  pointerEvents: 'none',
                }}
              />
            </Box>
          </Box>
        </Box>

        <RenderField
          label='Location ID'
          value={data.locationId}
          fieldKey='locationId'
          isEditing={isEditing}
          onChange={handleFieldChange}
        />
        <RenderField
          label='Title'
          value={data.title}
          fieldKey='title'
          isEditing={isEditing}
          onChange={handleFieldChange}
        />

        <RenderField
          label='Web Site'
          value={data.website}
          fieldKey='website'
          isEditing={isEditing}
          onChange={handleFieldChange}
          endNode={
            <IconButton
              onClick={() => handleOpenWeb(data.website)}
              disabled={!data.website || data.website.trim() === ''}
              sx={{
                width: '26px',
                height: '26px',
                border: `1px solid ${ORANGE_COLOR}`,
                borderRadius: '4px',
                color: DARK_BLUE,
                bgcolor: '#fff',
                '&:hover': { bgcolor: '#f0f0f0' },
                '&.Mui-disabled': {
                  opacity: 0.5,
                  borderColor: '#ccc',
                },
              }}>
              <LanguageIcon sx={{ fontSize: '18px' }} />
            </IconButton>
          }
        />

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Box sx={{ flex: 1 }}>
            <RenderField
              label='Lat'
              value={displayLat}
              fieldKey='lat'
              isEditing={false}
              onChange={() => {}}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <RenderField
              label='Lng'
              value={displayLng}
              fieldKey='lng'
              isEditing={false}
              onChange={() => {}}
              endNode={
                <IconButton
                  onClick={handleCopyLatLng}
                  disabled={displayLat === '' || displayLng === ''}
                  title='Copy Lat Lng'
                  sx={{
                    width: '26px',
                    height: '26px',
                    border: `1px solid ${ORANGE_COLOR}`,
                    borderRadius: '4px',
                    color: DARK_BLUE,
                    bgcolor: '#fff',
                    '&:hover': { bgcolor: '#f0f0f0' },
                    '&.Mui-disabled': {
                      opacity: 0.5,
                      borderColor: '#ccc',
                    },
                  }}>
                  <ContentCopyIcon sx={{ fontSize: '16px' }} />
                </IconButton>
              }
            />
          </Box>
        </Box>

        <RenderField
          label='Location Address'
          value={data.address}
          fieldKey='address'
          isEditing={isEditing}
          onChange={handleFieldChange}
          multiline
        />

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Box sx={{ flex: 1 }}>
            <RenderField
              label='City'
              value={data.city}
              fieldKey='city'
              isEditing={isEditing}
              onChange={handleFieldChange}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <RenderField
              label='State'
              value={data.state}
              fieldKey='state'
              isEditing={isEditing}
              onChange={handleFieldChange}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Box sx={{ flex: 1 }}>
            <RenderField
              label='PostCode / Zip'
              value={data.zip}
              fieldKey='zip'
              isEditing={isEditing}
              onChange={handleFieldChange}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <RenderField
              label='Country'
              value={data.country}
              fieldKey='country'
              isEditing={isEditing}
              onChange={handleFieldChange}
            />
          </Box>
        </Box>

        <RenderField
          label='Client Name'
          value={data.clientName}
          fieldKey='clientName'
          isEditing={isEditing}
          onChange={handleFieldChange}
        />
        <RenderSelect
          label='Category'
          editValue={data.categoryId}
          viewText={currentCategoryName}
          isEditing={isEditing}
          onChange={(v) => handleFieldChange('categoryId', v)}
          options={categories}
        />
        <RenderSelect
          label='Region'
          editValue={data.regionId}
          viewText={currentRegionName}
          isEditing={isEditing}
          onChange={(v) => handleFieldChange('regionId', v)}
          options={regions}
        />
        <RenderField
          label='Controller'
          value={data.controller}
          fieldKey='controller'
          isEditing={isEditing}
          onChange={handleFieldChange}
        />
        <RenderField
          label='Keywords'
          value={data.keywords}
          fieldKey='keywords'
          isEditing={isEditing}
          onChange={handleFieldChange}
          multiline
        />
        {activeMenu === 'MARKUP_MARKERS' && batchFilename && (
          <Box sx={{ mt: 1 }}>
            <RenderField
              label={
                batchSource === 'archives' ? 'Archived Batch Filename' : 'Batch Filename'
              }
              value={batchFilename}
              fieldKey='batchFilename'
              isEditing={false}
              onChange={() => {}}
              endNode={
                <IconButton
                  onClick={() => navigator.clipboard.writeText(batchFilename)}
                  title='Copy Batch Filename'
                  sx={{
                    width: '26px',
                    height: '26px',
                    border: `1px solid ${ORANGE_COLOR}`,
                    borderRadius: '4px',
                    color: DARK_BLUE,
                    bgcolor: '#fff',
                    '&:hover': { bgcolor: '#f0f0f0' },
                  }}>
                  <ContentCopyIcon sx={{ fontSize: '16px' }} />
                </IconButton>
              }
            />
          </Box>
        )}
      </Stack>

      <ConfirmDialog
        open={showUrlError}
        onClose={() => setShowUrlError(false)}
        message='The Web Site text does not appear to be a valid URL format.'
        severity='warning'
        autoClose={3000}
        disableRestoreFocus={true}
        cancelText={null}
        sx={{ zIndex: 100005 }} // AMENDED: Elevated to global dialog tier
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } }}
      />
    </Box>
  );
}
